import type {
  ApiError,
  RelatedPrompts,
  RelatedPromptsService,
} from '@empathyco/playboard-core-domain'
import type { ComputedRef, Ref, SetupContext } from 'vue'

import type Vue from 'vue'

import type {
  ContextState,
  Filter,
  RegularFilter,
  RegularFilterConfig,
  RegularFilterSelectedValues,
  RelatedPromptsMessages,
} from '../models'
import type { PlatformRelatedPromptsPreviewItems, RelatedPromptsPreviewService } from '../services'

import { computed, getCurrentInstance, ref } from 'vue'
import { CONTAINER } from '../config'
import { TYPES } from '../config/inversify.types'
import { FilterIdEnum, FilterTypeEnum } from '../enums'

import { CONTEXT_CONSTS } from '../store/context/context.consts'
import { FiltersUtils } from '../utils/filters.utils'
import { useApiError } from './use-api-error'
import { useFilters } from './use-filters'
import { mapActions, mapGetters } from './utils'

interface RelatedPromptsComposable {
  loading: Ref<boolean>
  setQuery: (query: string) => void
  context: ComputedRef<ContextState>
  filters: ComputedRef<Record<string, RegularFilter>>
  isSelectedMandatoryFilters: ComputedRef<boolean>
  messages: RelatedPromptsMessages
  setFiltersValues: (selectedValues: Record<string, RegularFilterSelectedValues>) => Promise<void>
  loadRelatedPrompts: () => Promise<RelatedPrompts[] | undefined>
  loadAllRelatedPrompts: () => Promise<RelatedPrompts[] | undefined>
  updateRelatedPrompt: (item: RelatedPrompts) => Promise<void>
  loadPreviewRelatedPromptsQueries: (
    query: string,
  ) => Promise<{ query: string; values: PlatformRelatedPromptsPreviewItems[] }>
  goToRoute: (newRoute: string) => void
}

export function useRelatedPrompts(ctx: SetupContext): RelatedPromptsComposable {
  const {
    proxy: { $store, $route, $router, $epb },
  } = getCurrentInstance() as { proxy: Vue }

  const { emit } = ctx

  const messages = $epb.messages.tools.relatedPrompts

  const service = CONTAINER.get<RelatedPromptsService>(TYPES.RelatedPromptsService)

  const { availableFilters } = useFilters()
  const { getErrorMessage } = useApiError()

  const relatedPromptsPreviewService = CONTAINER.get<RelatedPromptsPreviewService>(
    TYPES.RelatedPromptsPreviewService,
  )

  const { setFiltersSelectedValues } = mapActions($store, CONTEXT_CONSTS.moduleName, {
    setFiltersSelectedValues: CONTEXT_CONSTS.actions.setFiltersSelectedValues,
  })

  const { setQuery } = mapActions($store, CONTEXT_CONSTS.moduleName, {
    setQuery: CONTEXT_CONSTS.actions.setQuery,
  })

  const { context } = mapGetters($store, CONTEXT_CONSTS.moduleName, {
    context: CONTEXT_CONSTS.getters.context,
  })

  const langValue = ref()
  const loading = ref(false)

  const filters = computed(() => {
    const result: Record<string, RegularFilter> = {}

    Object.values(availableFilters.value)
      .filter((filter: Filter) => {
        return filter.config.type === FilterTypeEnum.REGULAR
      })
      .forEach((filter: Filter) => {
        const config = {
          ...filter.config,
          mandatory: filter.config.id === FilterIdEnum.LANG,
          multiselectDisabled: true,
        } as RegularFilterConfig

        if (config.values.length > 1) {
          const selectedValues = filter.selectedValues as RegularFilterSelectedValues

          result[config.id] = {
            config,
            selectedValues,
          }
        } else {
          setFiltersValues({
            [config.id]: [config.values[0].id],
          })
        }
      })
    return result
  })

  const isSelectedMandatoryFilters = computed(() =>
    Object.values(filters.value)
      .filter((filter: RegularFilter) => filter.config.mandatory)
      .every(
        (filter: RegularFilter) =>
          !!filter.selectedValues.length &&
          filter.config.values.some(({ id }) => id === filter.selectedValues[0]),
      ),
  )

  const lang = computed<string>(
    () => filters.value[FilterIdEnum.LANG]?.selectedValues[0] ?? langValue.value,
  )

  async function setFiltersValues(
    selectedValues: Record<string, RegularFilterSelectedValues>,
  ): Promise<void> {
    langValue.value = selectedValues?.lang[0]
    await setFiltersSelectedValues(selectedValues)
    emit('contextChanged')
  }

  const loadRelatedPrompts = async (): Promise<RelatedPrompts[] | undefined> => {
    try {
      loading.value = true
      const params = {
        lang: lang.value,
        query: context.value.query ?? '',
        pivots: Object.values(filters.value).reduce<Record<string, string>>((result, filter) => {
          if (filter.config.id !== FilterIdEnum.LANG && filter.selectedValues.length) {
            result[FiltersUtils.getEncodedId(filter.config)] = FiltersUtils.encodeValue(
              filter.config,
              filter.selectedValues[0],
            )
          }
          return result
        }, {}),
      }
      return await service.preview(params)
    } catch (error) {
      emit('showError', getErrorMessage(error as ApiError))
    } finally {
      loading.value = false
    }
  }

  const loadAllRelatedPrompts = async (): Promise<RelatedPrompts[] | undefined> => {
    try {
      loading.value = true
      const response = await service.list({
        page: { start: 0, rows: 1000 }, // TODO Add pagination in view
      })
      return response.results
    } catch (error) {
      emit('showError', getErrorMessage(error as ApiError))
    } finally {
      loading.value = false
    }
  }

  const updateRelatedPrompt = async (item: RelatedPrompts): Promise<void> => {
    try {
      loading.value = true
      await item.save()
    } catch (error) {
      emit('showError', getErrorMessage(error as ApiError))
    } finally {
      loading.value = false
    }
  }

  const loadPreviewRelatedPromptsQueries = async (
    query: string,
  ): Promise<{ query: string; values: PlatformRelatedPromptsPreviewItems[] }> => {
    return {
      query,
      values: await relatedPromptsPreviewService.listRelatedPromptsPreview({
        lang: lang.value,
        query,
      }),
    }
  }

  const goToRoute = (newRoute: string): void => {
    $router.push({
      ...$route,
      name: newRoute,
    })
  }

  return {
    loading,
    setQuery,
    context,
    filters,
    isSelectedMandatoryFilters,
    messages,
    setFiltersValues,
    loadRelatedPrompts,
    loadAllRelatedPrompts,
    updateRelatedPrompt,
    loadPreviewRelatedPromptsQueries,
    goToRoute,
  }
}
